import { TWidget } from 'widgets/Widget';
import { TRefElementInstance } from 'widgets/toolbox/RefElement';
import { TTabPanelInstance } from 'widgets/global/TabPanel';

const keyCode = Object.freeze({
    END: 35,
    HOME: 36,
    LEFT: 37,
    RIGHT: 39
});

type TButton = ReturnType<typeof import('widgets/global/Button').default>;
type TButtonInstance = InstanceType<TButton>;

export type TTabPanelChangeEvent = {
    activePanelName: string;
    previousActivePanelName: string;
};

/**
 * @description Base Tabs implementation
 * @param Widget - widget for extending
 * @returns Tabs widget
 */
function TabsClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory global
     * @class Tabs
     * @augments Widget
     * @property {string} data-widget - Widget name `tabs`
     * @property {boolean} data-active-first - activate first tab and first tab panel
     * @property {string} data-active-panel - activate tab and tab panel by provided panel id
     * @property {boolean} data-auto-activation - if tabs list should follow accessibility `Tabs with Automatic Activation` feature
     * @property {boolean} data-use-url-anchor - in case if true, each tab change will add a related hash to document location.
     * @classdesc Represents tabbed contents displaying and navigation.
     * Has the next features:
     * 1. Mark selected tab as active
     * 2. Handles keydown by enter\space keyboard button to manipulate the active tab
     * 3. Sets focus to the current tab
     * 4. Adds hash to document location if enabled
     *
     * @example <caption>Example of Tabs widget usage</caption>
     * <div
     *     data-widget="tabs"
     *     data-auto-activation="true"
     *     data-active-panel="login"
     * >
     *     <div
     *         class="b-tab_list m-account"
     *         data-ref="tablist"
     *         role="tablist"
     *         data-event-keydown="handleKeydown"
     *         aria-label="${Resource.msg('login.tablist.title.wai', 'login', null)}"
     *     >
     *         <button
     *             class="b-tab_list-tab m-active"
     *             aria-selected="true"
     *             data-tau="button_login"
     *             aria-controls="login-tab-panel"
     *             data-panel-name="login"
     *             data-widget-event-click="handleTabClick"
     *             data-id="button-login"
     *             data-event-click.prevent="handleClick"
     *             data-widget="button"
     *             role="tab"
     *             id="button-login"
     *         >
     *             ${Resource.msg('login.title', 'login', null)}
     *         </button>
     *         <button
     *             class="b-tab_list-tab"
     *             role="tab"
     *             id="button-register"
     *             tabindex="-1"
     *             aria-selected="false"
     *             data-tau="button_register"
     *             aria-controls="register-tab-panel"
     *             data-panel-name="register"
     *             data-widget-event-click="handleTabClick"
     *             data-id="button-register"
     *             data-event-click.prevent="handleClick"
     *             data-widget="button"
     *          >
     *              ${Resource.msg('login.title.newAccount', 'login', null)}
     *          </button>
     *     </div>
     *     <div
     *         role="tabpanel"
     *         class="b-tab_panel"
     *         id="login-tab-panel"
     *         aria-labelledby="button-login"
     *         data-ref="login"
     *         tabindex="0"
     *         data-tau="login_tab_panel_active"
     *     >
     *         ... tab content
     *     </div>
     *     <div
     *         role="tabpanel"
     *         class="b-tab_panel"
     *         id="register-tab-panel"
     *         aria-labelledby="button-register"
     *         data-ref="register"
     *         tabindex="0"
     *         data-tau="register_tab_panel_active"
     *     >
     *         ... tab content
     *     </div>
     * </div>
     */
    class Tabs extends Widget {
        panelNames: Array<string> = [];

        focusedTab = '';

        activePanel = '';

        prefs() {
            return {
                classesActive: 'm-active',
                activePanel: '',
                activeFirst: false,
                autoActivation: false,
                useUrlAnchor: false,
                ...super.prefs()
            };
        }

        /**
         * @description Refresh widget handler
         */
        onRefresh() {
            super.onRefresh();
            this.fulfillPanelNames();
        }

        /**
         * @description Widget initialization logic
         */
        init() {
            super.init();
            this.fulfillPanelNames();

            if (this.prefs().activePanel) {
                this.activatePanel(this.prefs().activePanel);
            } else if (this.prefs().useUrlAnchor) {
                this.handleUrlChange();
            } else if (this.prefs().activeFirst && this.panelNames && this.panelNames.length) {
                this.activatePanel(this.panelNames[0]);
            }
        }

        /**
         * @description Registers all available panels by adding their IDs into array.
         */
        fulfillPanelNames() {
            this.panelNames = [];
            const Button: TButton = <TButton> this.getConstructor('button');

            this.eachChild(child => {
                if (child instanceof Button && this.panelNames) {
                    this.panelNames.push(String(child.config.panelName));
                }
            });
        }

        /**
         * @param clickedButton - Widget, representing customer's tab clicked element
         */
        handleTabClick(clickedButton: InstanceType<TWidget>) {
            this.activatePanel(String(clickedButton.config.panelName));
        }

        /**
         * @param panelName name of panel to activate
         * @param saveToPushState - If we need to save hash in history
         */
        activatePanel(panelName: string, saveToPushState = true) {
            if (!this.panelNames) {
                return;
            }

            const previousActivePanelName = this.activePanel;

            this.panelNames.forEach(id => {
                const isActive = id === panelName;

                const currentTabPanel = this.toggleTabPanel(id, isActive, saveToPushState);

                if (!currentTabPanel && this.ref(id)) {
                    this.toggleActiveRefPanel(this.ref(id), isActive);

                    if (isActive && saveToPushState) {
                        this.handleUrlAnchor(id);
                    }
                }

                if (isActive) {
                    this.activePanel = panelName;
                    this.focusedTab = panelName;

                    this.getById<TButtonInstance>(`button-${id}`, (button) => {
                        if(button.ref('self').get()?.hasAttribute('data-focus-content-id')) {
                            this.setFocusToContent(button);
                        }
                    });
                }

                this.toggleButton(id, isActive);
            });

            const eventData: TTabPanelChangeEvent = {
                activePanelName: panelName,
                previousActivePanelName
            };

            this.emit('panelactivated', eventData);
        }

        /**
         * @description Toggle tab panel
         * @param id - panel ID
         * @param isActive - Is current one should be active
         * @param saveToPushState - Is state should be added to browser history
         * @returns Tab instance
         */
        toggleTabPanel(id: string, isActive: boolean, saveToPushState: boolean): TTabPanelInstance | undefined {
            return this.getById<TTabPanelInstance, TTabPanelInstance>(id, (tabPanel) => {
                tabPanel[isActive ? 'activate' : 'deactivate']();

                if (isActive && saveToPushState) {
                    this.handleUrlAnchor(id);
                }

                return tabPanel;
            });
        }

        /**
         * @description Toggle tab button
         * @param id - panel ID
         * @param isActive - Is current should be active
         */
        toggleButton(id: string, isActive: boolean) {
            this.getById<TButtonInstance>(`button-${id}`, (button) => {
                if (isActive) {
                    button.activate();
                    button.select();
                } else {
                    button.deactivate();
                    button.unselect();
                }
            });
        }

        /**
         * @description In addition to tab switch, action will be reflected in URL anchor, if enabled.
         * This will further allow to select proper tab, when returning back from browser history.
         * @param tabName tab name to reflect in browser location hash.
         */
        handleUrlAnchor(tabName: string) {
            if (this.prefs().useUrlAnchor) {
                window.history.pushState({
                    hashChangedOnly: true
                }, document.title, `#${tabName}`);
            }
        }

        /**
         * @description Triggers needed tab activation, when URL changed
         * in case of history push/replace state and using browser's `back` button.
         * @param saveToPushState - If we need to save hash in history
         */
        handleUrlChange(saveToPushState = true) {
            if (document.location.hash) {
                const panelToActivate = this.panelNames
                    && this.panelNames.find(panelName => panelName === document.location.hash.replace('#', ''));

                if (panelToActivate) {
                    this.activatePanel(panelToActivate, saveToPushState);
                }
            }
        }

        /**
         * @description Method to toggle active panel, if it is not a separate widget `TabPanel`
         * but an ordinary `ref` element inside `Tabs` widget.
         * @param panel target panel name
         * @param state is target panel is selected or not
         */
        toggleActiveRefPanel(panel: TRefElementInstance, state: boolean) {
            panel.toggleClass(this.prefs().classesActive, state);
        }

        /**
         * @description Gets last focused panel name
         * <br>(focused panel is not necessarily active panel)
         * @returns Focused panel name if founded. Empty string otherwise
         */
        getLastFocusedTab(): string {
            return this.focusedTab
                ? this.focusedTab
                : this.activePanel || (this.panelNames && this.panelNames[0]) || '';
        }

        /**
         * @description Sets focus to panel with given name
         * <br>Uses `roving focus` accessibility feature
         * <br>https://www.w3.org/TR/wai-aria-practices/#kbd_roving_tabindex
         * @param tab Tab name to set focus to
         */
        setFocusToTab(tab: string) {
            if (!tab) {
                return;
            }

            this.focusedTab = tab;

            if (this.panelNames) {
                this.panelNames.forEach(id => {
                    const isTargetTab = id === tab;

                    this.getById<TButtonInstance>(`button-${id}`, (button) => {
                        if (isTargetTab) {
                            button.focus().setTabIndex();
                        } else {
                            button.unsetTabIndex();
                        }
                    });
                });
            }

            if (this.prefs().autoActivation) {
                this.activatePanel(tab);
            }
        }

        /**
         * @description Sets focus to the very first panel
         */
        setFocusToFirstTab() {
            const firstTab = (this.panelNames
                && this.panelNames.length
                && this.panelNames[0])
                || '';

            this.setFocusToTab(firstTab);
        }

        /**
         * @description Sets focus to the very last panel
         */
        setFocusToLastTab() {
            const lastTab = (this.panelNames
                && this.panelNames.length
                && this.panelNames[this.panelNames.length - 1])
                || '';

            this.setFocusToTab(lastTab);
        }

        /**
         * @description Sets focus to previous panel. Loops focus, if first panel reached
         */
        setFocusToPreviousTab() {
            if (this.panelNames && this.panelNames.length) {
                const currentFocusedTab = this.getLastFocusedTab();
                const currentFocusedTabIndex = this.panelNames.indexOf(currentFocusedTab);
                const previousTab = currentFocusedTabIndex === 0
                    ? this.panelNames[this.panelNames.length - 1]
                    : this.panelNames[currentFocusedTabIndex - 1];

                this.setFocusToTab(previousTab);
            }
        }

        /**
         * @description Sets focus to next panel. Loops focus, if last panel reached
         */
        setFocusToNextTab() {
            if (this.panelNames && this.panelNames.length) {
                const currentFocusedTab = this.getLastFocusedTab();
                const currentFocusedTabIndex = this.panelNames.indexOf(currentFocusedTab);
                const nextTab = currentFocusedTabIndex === this.panelNames.length - 1
                    ? this.panelNames[0]
                    : this.panelNames[currentFocusedTabIndex + 1];

                this.setFocusToTab(nextTab);
            }
        }

        /**
         * @description Keydown Event handler
         * @param _ Source of keydown event
         * @param event  Event object
         */
        handleKeydown(_: HTMLElement, event: KeyboardEvent) {
            let preventEventActions = false;

            switch (event.keyCode) {
                case keyCode.HOME:
                    this.setFocusToFirstTab();
                    preventEventActions = true;
                    break;
                case keyCode.END:
                    this.setFocusToLastTab();
                    preventEventActions = true;
                    break;
                case keyCode.LEFT:
                    this.setFocusToPreviousTab();
                    preventEventActions = true;
                    break;
                case keyCode.RIGHT:
                    this.setFocusToNextTab();
                    preventEventActions = true;
                    break;
                default:
                    break;
            }

            if (preventEventActions) {
                event.preventDefault();
                event.stopPropagation();
            }
        }

        /**
         * @description Sets focus to content of the active tab
         * @param button - Active button, which contains `data-focus-content-id` attribute
         */
        setFocusToContent(button: TButtonInstance) {
            const contentId = button.ref('self').get()?.getAttribute('data-focus-content-id');

            if (!contentId) {
                return;
            }

            this.getByIdDeep(contentId, (el) => {
                el.ref('self').get()?.focus();
            });
        }
    }

    return Tabs;
}

export type TTabs = ReturnType<typeof TabsClassCreator>;

export type TTabsInstance = InstanceType<TTabs>;

export default TabsClassCreator;
